import React, { useEffect, useMemo, useRef, useState } from 'react';
import { portalCopy } from '../copy';
import { BUSINESS_DESCRIPTION_MAX_LENGTH } from '../constants';
import type { BusinessClass, PartnerBusiness, WizardPreviewDraft } from '../types';
import { hasValidBusinessClasses } from '../utils/businessClasses';
import BusinessHoursReadOnly from './BusinessHoursReadOnly';
import WizardClassesEditor from './WizardClassesEditor';
import { PortalButton, PortalCheckbox, PortalField, portalInputClass } from './PortalUi';

type WizardStepProfileProps = {
  business: PartnerBusiness;
  mode?: 'wizard' | 'edit';
  onSubmit: (
    patch: Pick<
      PartnerBusiness,
      'description' | 'website' | 'instagram' | 'hours' | 'hoursEnabled' | 'classes' | 'photoCount' | 'photoPreviewUrls'
    >,
  ) => void;
  onBack: () => void;
  onPreviewChange?: (draft: WizardPreviewDraft) => void;
};

const MAX_PHOTOS = 9;

type WellnessProfileTab = 'classes' | 'hours';

type PhotoPreviewItem = {
  id: string;
  url: string;
  source: 'imported' | 'upload';
};

function createPhotoId(): string {
  return `photo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function buildInitialPhotos(urls: string[] = []): PhotoPreviewItem[] {
  return urls.map((url, index) => ({
    id: `photo_${index}_${url.slice(-12)}`,
    url,
    source: url.startsWith('blob:') ? ('upload' as const) : ('imported' as const),
  }));
}

const WizardStepProfile: React.FC<WizardStepProfileProps> = ({
  business,
  mode = 'wizard',
  onSubmit,
  onBack,
  onPreviewChange,
}) => {
  const isEditMode = mode === 'edit';
  const isWellness = business.category === 'wellness';
  const [description, setDescription] = useState(() =>
    business.description.slice(0, BUSINESS_DESCRIPTION_MAX_LENGTH),
  );
  const [website, setWebsite] = useState(business.website);
  const [instagram, setInstagram] = useState(business.instagram);
  const [hoursEnabled, setHoursEnabled] = useState(business.hoursEnabled ?? true);
  const [wellnessTab, setWellnessTab] = useState<WellnessProfileTab>('classes');
  const [classes, setClasses] = useState<BusinessClass[]>(business.classes ?? []);
  const [photos, setPhotos] = useState<PhotoPreviewItem[]>(() =>
    buildInitialPhotos(business.photoPreviewUrls ?? business.importedPhotoUrls),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const photoPreviewUrls = useMemo(() => photos.map((photo) => photo.url), [photos]);
  const photoCount = photos.length;

  useEffect(() => {
    onPreviewChange?.({
      description,
      website,
      instagram,
      hours: business.hours,
      hoursEnabled: isWellness ? hoursEnabled : true,
      classes: isWellness ? classes : [],
      photoCount,
      photoPreviewUrls,
    });
  }, [
    description,
    website,
    instagram,
    business.hours,
    hoursEnabled,
    classes,
    isWellness,
    photoCount,
    photoPreviewUrls,
    onPreviewChange,
  ]);

  const canSubmit =
    description.trim().length >= (isEditMode ? 10 : 20) &&
    photos.length >= 1 &&
    (!isWellness || hasValidBusinessClasses(classes));

  const handlePhotos = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) return;

    const added = Array.from(files)
      .slice(0, remaining)
      .map((file) => ({
        id: createPhotoId(),
        url: URL.createObjectURL(file),
        source: 'upload' as const,
      }));

    setPhotos((prev) => [...prev, ...added]);
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const target = prev.find((photo) => photo.id === id);
      if (target?.source === 'upload') URL.revokeObjectURL(target.url);
      return prev.filter((photo) => photo.id !== id);
    });
  };

  const setCoverPhoto = (id: string) => {
    setPhotos((prev) => {
      const index = prev.findIndex((photo) => photo.id === id);
      if (index <= 0) return prev;
      const next = [...prev];
      const [cover] = next.splice(index, 1);
      next.unshift(cover);
      return next;
    });
  };

  const hasImportedPhotos = photos.some((photo) => photo.source === 'imported');

  const hoursPanel = (
    <div>
      <p className="mb-3 font-jakarta text-xs leading-relaxed text-[#5a3158]">
        {portalCopy.wizard.profile.hoursSyncNote}
      </p>

      {isWellness && (
        <div className="mb-3 rounded-xl bg-[#fcf7fa] px-3 py-3">
          <PortalCheckbox
            id="hours-enabled"
            checked={hoursEnabled}
            onChange={(e) => setHoursEnabled(e.target.checked)}
            label={
              <span className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
                {portalCopy.wizard.profile.hoursToggleWellness}
              </span>
            }
          />
        </div>
      )}

      {(!isWellness || hoursEnabled) && <BusinessHoursReadOnly hours={business.hours} />}
    </div>
  );

  return (
    <div className="space-y-6">
      {!isEditMode && (
        <div>
          <h2 className="font-instrument text-3xl italic tracking-[-0.01em] text-[#2a0f2a]">
            {portalCopy.wizard.profile.title}
          </h2>
          <p className="mt-2 font-jakarta text-[15px] leading-relaxed text-[#5a3158]">
            {isWellness ? portalCopy.wizard.profile.subtitleWellness : portalCopy.wizard.profile.subtitleFood}
          </p>
        </div>
      )}

      <div className="space-y-5 rounded-[24px] border border-[rgba(74,33,71,0.1)] bg-white p-6">
        <PortalField
          label={portalCopy.wizard.profile.description}
          htmlFor="business-description"
          hint={portalCopy.wizard.profile.descriptionMaxHint}
        >
          <textarea
            id="business-description"
            rows={4}
            maxLength={BUSINESS_DESCRIPTION_MAX_LENGTH}
            className={`${portalInputClass} resize-y`}
            value={description}
            onChange={(e) =>
              setDescription(e.target.value.slice(0, BUSINESS_DESCRIPTION_MAX_LENGTH))
            }
            placeholder={portalCopy.wizard.profile.descriptionPlaceholder}
          />
          <p
            className={`mt-1.5 text-right font-jakarta text-xs font-semibold ${
              description.length >= BUSINESS_DESCRIPTION_MAX_LENGTH
                ? 'text-chisme-primary'
                : 'text-[#5a3158]'
            }`}
            aria-live="polite"
          >
            {portalCopy.wizard.profile.descriptionCharCount(
              description.length,
              BUSINESS_DESCRIPTION_MAX_LENGTH,
            )}
          </p>
        </PortalField>

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <span className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
              {portalCopy.wizard.profile.photos}
            </span>
            <span className="font-jakarta text-xs font-semibold text-chisme-primary">
              {portalCopy.wizard.profile.photosSelected(photos.length, MAX_PHOTOS)}
            </span>
          </div>

          <div className="rounded-2xl border-2 border-dashed border-[rgba(74,33,71,0.2)] bg-[#fcf7fa] p-4">
            {photos.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <span className="text-2xl">📷</span>
                <p className="mt-2 max-w-sm font-jakarta text-sm text-[#5a3158]">
                  {hasImportedPhotos
                    ? portalCopy.wizard.profile.photosImportedHint
                    : portalCopy.wizard.profile.photosHint}
                </p>
                <PortalButton
                  type="button"
                  className="mt-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {portalCopy.wizard.profile.photosAddMore}
                </PortalButton>
              </div>
            ) : (
              <div className="space-y-4">
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {photos.map((photo, index) => (
                    <li
                      key={photo.id}
                      className={`group relative aspect-[4/3] overflow-hidden rounded-xl bg-[#f2e8f0] ${
                        index === 0 ? 'ring-2 ring-chisme-primary ring-offset-2 ring-offset-[#fcf7fa]' : ''
                      }`}
                    >
                      <img src={photo.url} alt="" className="h-full w-full object-cover" />
                      {index === 0 ? (
                        <span className="absolute left-2 top-2 rounded-full bg-chisme-primary px-2 py-0.5 font-jakarta text-[10px] font-bold text-white">
                          {portalCopy.wizard.profile.photosCover}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCoverPhoto(photo.id)}
                          className="absolute bottom-2 left-2 rounded-full bg-white/95 px-2.5 py-1 font-jakarta text-[10px] font-semibold text-[#2a0f2a] opacity-100 shadow-sm transition-opacity hover:bg-white sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                        >
                          {portalCopy.wizard.profile.photosSetCover}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(photo.id)}
                        aria-label={portalCopy.wizard.profile.photosRemove}
                        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-[#2a0f2a]/75 text-white opacity-100 transition-opacity hover:bg-[#2a0f2a] sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                          <path
                            d="M2.5 2.5l7 7M9.5 2.5l-7 7"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
                {photos.length < MAX_PHOTOS && (
                  <PortalButton
                    type="button"
                    variant="secondary"
                    fullWidth
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {portalCopy.wizard.profile.photosAddMore}
                  </PortalButton>
                )}
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handlePhotos(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <PortalField label={portalCopy.wizard.profile.instagram} htmlFor="business-instagram">
            <input
              id="business-instagram"
              className={portalInputClass}
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder={portalCopy.wizard.profile.instagramPlaceholder}
            />
          </PortalField>

          <PortalField label={portalCopy.wizard.profile.website} htmlFor="business-website">
            <input
              id="business-website"
              className={portalInputClass}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder={portalCopy.wizard.profile.websitePlaceholder}
            />
          </PortalField>
        </div>

        {isWellness ? (
          <div className="border-t border-[rgba(74,33,71,0.08)] pt-5">
            <div
              role="tablist"
              aria-label={portalCopy.wizard.profile.title}
              className="mb-5 flex gap-1 rounded-2xl bg-[#fcf7fa] p-1"
            >
              {(['classes', 'hours'] as const).map((tab) => {
                const isActive = wellnessTab === tab;
                const label =
                  tab === 'classes'
                    ? portalCopy.wizard.profile.tabs.classes
                    : portalCopy.wizard.profile.tabs.hours;

                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    id={`wellness-tab-${tab}`}
                    aria-selected={isActive}
                    aria-controls={`wellness-panel-${tab}`}
                    onClick={() => setWellnessTab(tab)}
                    className={`flex-1 rounded-xl px-4 py-2.5 font-jakarta text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-white text-[#2a0f2a] shadow-sm'
                        : 'text-[#5a3158] hover:text-[#2a0f2a]'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {wellnessTab === 'classes' && (
              <div
                role="tabpanel"
                id="wellness-panel-classes"
                aria-labelledby="wellness-tab-classes"
              >
                <WizardClassesEditor embedded classes={classes} onChange={setClasses} />
              </div>
            )}

            {wellnessTab === 'hours' && (
              <div role="tabpanel" id="wellness-panel-hours" aria-labelledby="wellness-tab-hours">
                <div className="mb-2">
                  <span className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
                    {portalCopy.wizard.profile.hoursFromGoogle}
                  </span>
                </div>
                {hoursPanel}
              </div>
            )}
          </div>
        ) : (
          <div className="border-t border-[rgba(74,33,71,0.08)] pt-5">
            <div className="mb-2">
              <span className="font-jakarta text-sm font-semibold text-[#2a0f2a]">
                {portalCopy.wizard.profile.hoursFromGoogle}
              </span>
            </div>
            {hoursPanel}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <PortalButton variant="secondary" fullWidth onClick={onBack}>
          {isEditMode ? portalCopy.business.edit.cancel : portalCopy.wizard.back_step}
        </PortalButton>
        <PortalButton
          fullWidth
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              description: description.trim(),
              website: website.trim(),
              instagram: instagram.trim(),
              hours: business.hours,
              hoursEnabled: isWellness ? hoursEnabled : true,
              classes: isWellness ? classes : [],
              photoCount: photos.length,
              photoPreviewUrls,
            })
          }
        >
          {isEditMode ? portalCopy.business.edit.save : portalCopy.wizard.next}
        </PortalButton>
      </div>
    </div>
  );
};

export default WizardStepProfile;
